import { JOURNAL_SECTIONS, type RevertTarget } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Download, SlidersHorizontal } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ActionSheet } from './journal/ActionSheet';
import { IntegrityPanel } from './journal/IntegrityPanel';
import { ActionsList, AttemptsList, ChangesList, DeletedList, RevertsList } from './journal/lists';
import { parseRecord, RecordSheet } from './journal/RecordSheet';
import { RevertDialog } from './journal/RevertDialog';
import {
  activeFilterCount,
  filtersQuery,
  type JournalFilters,
  type JournalMeta,
  type JournalQueryParams,
  NO_FILTERS,
} from './journal/shared';

const TABS = [
  { key: 'actions', title: 'Действия' },
  { key: 'changes', title: 'Изменения' },
  { key: 'deleted', title: 'Удалённое' },
  { key: 'reverts', title: 'Отмены' },
  { key: 'attempts', title: 'Отказы' },
  { key: 'integrity', title: 'Проверка базы' },
] as const;
type Tab = (typeof TABS)[number]['key'];

/** Какие поля отбора есть на вкладке */
const FILTERS_OF: Record<Tab, Array<keyof JournalFilters>> = {
  actions: ['q', 'actorId', 'section', 'projectId', 'op', 'from', 'to', 'system'],
  changes: ['q', 'actorId', 'section', 'projectId', 'op', 'from', 'to', 'system'],
  deleted: ['q', 'actorId', 'section', 'projectId', 'from', 'to'],
  attempts: ['actorId', 'from', 'to'],
  reverts: [],
  integrity: [],
};

type Search = { tab?: string; record?: string; act?: string };

function FilterBar({
  tab,
  filters,
  onChange,
  meta,
}: {
  tab: Tab;
  filters: JournalFilters;
  onChange: (f: JournalFilters) => void;
  meta: JournalMeta | undefined;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(filters.q);
  const has = (k: keyof JournalFilters) => FILTERS_OF[tab].includes(k);
  // Поиск — после паузы в наборе: не запрос на каждую букву
  useEffect(() => {
    if (text === filters.q) return;
    const t = setTimeout(() => onChange({ ...filters, q: text }), 400);
    return () => clearTimeout(t);
  }, [text, filters, onChange]);
  const visible: JournalFilters = Object.fromEntries(
    Object.entries(filters).map(([k, v]) => [
      k,
      has(k as keyof JournalFilters) ? v : NO_FILTERS[k as keyof JournalFilters],
    ]),
  ) as JournalFilters;
  const active = activeFilterCount(visible);
  const set = (patch: Partial<JournalFilters>) => onChange({ ...filters, ...patch });
  return (
    <div className="border-b border-line px-5 py-3">
      <div className="flex flex-wrap items-center gap-2 md:hidden">
        <Button size="sm" onClick={() => setOpen((v) => !v)} icon={<SlidersHorizontal className="size-4" />}>
          {open ? 'Скрыть отбор' : `Отбор${active ? ` (${active})` : ''}`}
        </Button>
      </div>
      <div className={clsx('gap-3 sm:grid-cols-2 lg:grid-cols-4', open ? 'mt-3 grid' : 'hidden md:grid')}>
        {has('q') ? (
          <Field label="Поиск по значениям" htmlFor={`${id}-q`}>
            <Input
              id={`${id}-q`}
              type="search"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Номер, название, текст"
            />
          </Field>
        ) : null}
        {has('actorId') ? (
          <Field label="Сотрудник" htmlFor={`${id}-a`}>
            <Select id={`${id}-a`} value={filters.actorId} onChange={(e) => set({ actorId: e.target.value })}>
              <option value="">Все</option>
              {meta?.actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.fullName}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {has('section') ? (
          <Field label="Раздел" htmlFor={`${id}-s`}>
            <Select id={`${id}-s`} value={filters.section} onChange={(e) => set({ section: e.target.value })}>
              <option value="">Все разделы</option>
              {Object.entries(JOURNAL_SECTIONS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {has('projectId') ? (
          <Field label="Проект" htmlFor={`${id}-p`}>
            <Select
              id={`${id}-p`}
              value={filters.projectId}
              onChange={(e) => set({ projectId: e.target.value })}
            >
              <option value="">Все проекты</option>
              {meta?.projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {has('op') ? (
          <Field label="Что сделано" htmlFor={`${id}-o`}>
            <Select id={`${id}-o`} value={filters.op} onChange={(e) => set({ op: e.target.value })}>
              <option value="">Всё</option>
              <option value="I">Создание</option>
              <option value="U">Изменение</option>
              <option value="D">Удаление</option>
            </Select>
          </Field>
        ) : null}
        {has('from') ? (
          <Field label="С даты" htmlFor={`${id}-f`}>
            <Input
              id={`${id}-f`}
              type="date"
              value={filters.from}
              onChange={(e) => set({ from: e.target.value })}
            />
          </Field>
        ) : null}
        {has('to') ? (
          <Field label="По дату" htmlFor={`${id}-t`}>
            <Input
              id={`${id}-t`}
              type="date"
              value={filters.to}
              onChange={(e) => set({ to: e.target.value })}
            />
          </Field>
        ) : null}
        {has('system') ? (
          <div className="flex items-end pb-2">
            <Checkbox
              label="Изменения самого портала"
              description="обмен с 1С, фоновые задачи"
              checked={filters.system}
              onChange={(e) => set({ system: e.target.checked })}
            />
          </div>
        ) : null}
        {active ? (
          <div className="flex items-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setText('');
                onChange(NO_FILTERS);
              }}
            >
              Сбросить отбор
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Ссылка на выгрузку в Excel по тому же отбору */
function exportHref(query: JournalQueryParams): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) qs.set(k, v);
  const s = qs.toString();
  return `/api/v1/admin/journal/export.xlsx${s ? `?${s}` : ''}`;
}

/**
 * Журнал изменений компании (ДС-11, НФ-12): кто, когда, откуда и что изменил; отмена действия целиком,
 * восстановление удалённого, состояние любой записи на любой момент и возврат к нему, проверка базы.
 */
export function AuditPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const tab: Tab = TABS.find((t) => t.key === search.tab)?.key ?? 'actions';
  const meta = useQuery({
    queryKey: ['journal', 'meta'],
    queryFn: () => api<JournalMeta>('/admin/journal/meta'),
    staleTime: 60_000,
  });
  const canRevert = Boolean(meta.data?.canRevert);
  const [filters, setFilters] = useState<JournalFilters>(NO_FILTERS);
  const [revert, setRevert] = useState<{ target: RevertTarget; title: string; description?: string } | null>(
    null,
  );
  const record = parseRecord(search.record);

  // Отбор вкладки: поля, которых на вкладке нет, в запрос не попадают
  const query = useMemo(() => {
    const all = filtersQuery(filters);
    const keys = FILTERS_OF[tab];
    return Object.fromEntries(Object.entries(all).filter(([k]) => keys.includes(k as keyof JournalFilters)));
  }, [filters, tab]);

  // Открытие панели — новый шаг истории (кнопка «назад» её закрывает), закрытие — замена шага
  const go = (patch: Search, replace = false) =>
    void navigate({ to: '/admin/audit', search: { ...search, ...patch }, replace });
  const handlers = {
    canRevert,
    onAction: (key: string) => go({ act: key, record: undefined }),
    onRecord: (r: string) => go({ record: r, act: undefined }),
    onRevert: (target: RevertTarget, title: string, description?: string) =>
      setRevert({ target, title, description }),
  };

  return (
    <>
      <Panel
        title="Журнал изменений"
        meta="кто, когда и что изменил; отмена и восстановление с проверкой"
        actions={
          tab === 'actions' || tab === 'changes' ? (
            <a
              href={exportHref(query)}
              className="inline-flex h-11 items-center gap-1.5 rounded-md border border-line-strong bg-surface px-3 text-[13px] font-medium text-ink hover:underline md:h-8"
            >
              <Download className="size-4" aria-hidden />
              Excel
            </a>
          ) : null
        }
        bodyClassName="p-0"
      >
        <div className="border-b border-line px-5 py-3">
          <div
            className="flex flex-wrap gap-1 rounded-md border border-line bg-surface p-0.5"
            role="tablist"
            aria-label="Журнал изменений"
          >
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => go({ tab: t.key === 'actions' ? undefined : t.key })}
                className={clsx(
                  'h-11 min-w-11 rounded-[5px] px-3 text-[13px] font-medium md:h-8',
                  tab === t.key ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
                )}
              >
                {t.title}
              </button>
            ))}
          </div>
        </div>
        {FILTERS_OF[tab].length ? (
          <FilterBar tab={tab} filters={filters} onChange={setFilters} meta={meta.data} />
        ) : null}
        {tab === 'actions' ? <ActionsList query={query} {...handlers} /> : null}
        {tab === 'changes' ? <ChangesList query={query} {...handlers} /> : null}
        {tab === 'deleted' ? <DeletedList query={query} {...handlers} /> : null}
        {tab === 'reverts' ? <RevertsList {...handlers} /> : null}
        {tab === 'attempts' ? <AttemptsList query={query} /> : null}
        {tab === 'integrity' ? (
          <div className="p-5">
            <IntegrityPanel />
          </div>
        ) : null}
      </Panel>

      {search.act ? (
        <ActionSheet
          actionKey={search.act}
          canRevert={canRevert}
          onClose={() => go({ act: undefined }, true)}
          onRecord={handlers.onRecord}
          onRevert={handlers.onRevert}
        />
      ) : null}
      {record ? (
        <RecordSheet
          record={record}
          canRevert={canRevert}
          onClose={() => go({ record: undefined }, true)}
          onAction={handlers.onAction}
          onRevert={handlers.onRevert}
        />
      ) : null}
      {revert ? (
        <RevertDialog
          key={JSON.stringify(revert.target)}
          target={revert.target}
          title={revert.title}
          description={revert.description}
          onClose={() => setRevert(null)}
        />
      ) : null}
    </>
  );
}
